import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const HERO_IMG = "https://images.unsplash.com/photo-1600851555921-d280ba3f019a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob3NwaXRhbCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODM4Mjc2MDJ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/users").then(({ data }) => setUsers(data)).catch(() => toast.error("No fue posible cargar los usuarios"));
  }, []);

  const handleLogin = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await login(selected.email);
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden grain">
        <img src={HERO_IMG} alt="UC CHRISTUS" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-hero opacity-90" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-background/10 border border-background/20 backdrop-blur flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-background" />
            </div>
            <div>
              <p className="font-display font-semibold text-background">AI Governance Platform</p>
              <p className="text-[11px] tracking-[0.2em] uppercase text-background/70">UC CHRISTUS</p>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl font-semibold text-background leading-tight">
              Gobierno institucional de la inteligencia artificial
            </h1>
            <p className="mt-4 text-background/80 leading-relaxed text-sm">
              Detecte, registre, evalúe, decida, monitoree y reevalúe todo el ecosistema de IA de la organización con trazabilidad completa.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["AI Registry", "Shadow AI", "Risk Assessment", "Comité de IA", "Auditoría"].map((t) => (
                <span key={t} className="px-3 py-1 rounded-full border border-background/25 text-background/85 text-xs font-medium bg-background/5 backdrop-blur">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <p className="text-xs text-background/50">Red de Salud UC CHRISTUS · Dirección de Gobernanza de IA</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md animate-in-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-primary-foreground" />
            </div>
            <p className="font-display font-semibold">AI Governance · UC CHRISTUS</p>
          </div>
          <h2 className="font-display text-2xl font-semibold">Acceso a la plataforma</h2>
          <p className="text-sm text-muted-foreground mt-1.5">Ambiente de demostración · seleccione un perfil institucional para continuar.</p>

          <div className="mt-8 space-y-2">
            {users.map((u) => (
              <button
                key={u.id}
                onClick={() => setSelected(u)}
                data-testid={`login-user-${u.role}${u.id === "u-com2" || u.id === "u-com3" ? "-" + u.id : ""}`}
                className={cn(
                  "w-full flex items-center gap-4 rounded-xl border p-3.5 text-left transition-colors",
                  selected?.id === u.id ? "border-accent bg-accent-soft shadow-card" : "border-border bg-card hover:border-accent/50"
                )}
              >
                <Avatar className="h-10 w-10">
                  <AvatarFallback className={cn("text-xs font-semibold", selected?.id === u.id ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground")}>
                    {u.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.title}</p>
                </div>
                <Badge variant="outline" className="shrink-0 bg-primary/8 text-primary border-primary/20 text-[11px]">{ROLE_LABEL[u.role]}</Badge>
              </button>
            ))}
          </div>

          <Button
            className="w-full mt-6 h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
            disabled={!selected || loading}
            onClick={handleLogin}
            data-testid="login-submit-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Ingresar a la plataforma <ArrowRight className="h-4 w-4 ml-2" /></>)}
          </Button>
          <p className="text-[11px] text-muted-foreground/70 text-center mt-4">Acceso auditado · cada inicio de sesión queda registrado en la bitácora institucional</p>
        </div>
      </div>
    </div>
  );
}
